import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Platform, Profile } from "@/types/profile";
import { PayloadTwoPanelSelector } from "./PayloadTwoPanelSelector";
import { PayloadConfigForm } from "./PayloadConfigForm";
import { ProfileAssignment } from "./ProfileAssignment";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { getPayloadById } from "@/utils/payloads";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string().optional(),
  platforms: z.array(z.string()).min(1, { message: "At least one platform must be selected" }),
});

type FormValues = z.infer<typeof formSchema>;

interface ProfileFormProps {
  initialData?: Profile;
  isEdit?: boolean;
  onSave?: (profile: Omit<Profile, "id" | "createdAt" | "updatedAt">, isDraft?: boolean) => void;
}

export const ProfileForm = ({ initialData, isEdit = false, onSave }: ProfileFormProps) => {
  const navigate = useNavigate();
  
  // Form state
  const [step, setStep] = useState<"details" | "assignment">("details");
  
  // Payloads state
  const [selectedPayloads, setSelectedPayloads] = useState<string[]>(
    initialData?.payloads.map(p => p.payloadId) || []
  );
  
  const [payloadConfigs, setPayloadConfigs] = useState<Record<string, Record<string, any>>>(
    initialData?.payloads.reduce((acc, payload) => {
      acc[payload.payloadId] = payload.configValues;
      return acc;
    }, {} as Record<string, Record<string, any>>) || {}
  );

  const [activePayloadId, setActivePayloadId] = useState<string | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [pendingPayloadId, setPendingPayloadId] = useState<string | null>(null);
  const [configValidationState, setConfigValidationState] = useState<boolean>(true);
  
  // Customer and groups state
  const [customers, setCustomers] = useState<string[]>(initialData?.customer ? [initialData.customer] : []);
  const [groups, setGroups] = useState<string[]>(initialData?.groups || []);
  const [tags, setTags] = useState<string[]>(initialData?.tags || []);
  
  // Track payloads requiring configuration
  const [unconfiguredPayloads, setUnconfiguredPayloads] = useState<string[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      platforms: initialData?.platforms || [],
    },
  });

  const platforms: { id: Platform; label: string }[] = [
    { id: "macos", label: "macOS" },
    { id: "ios", label: "iOS" },
    { id: "ipados", label: "iPadOS" },
    { id: "tvos", label: "tvOS" },
  ];

  // Check which payloads need configuration
  useEffect(() => {
    const unconfigured = selectedPayloads.filter(payloadId => {
      const payload = getPayloadById(payloadId);
      return payload?.configFields.length > 0 && 
        (!payloadConfigs[payloadId] || Object.keys(payloadConfigs[payloadId]).length === 0);
    });
    setUnconfiguredPayloads(unconfigured);
  }, [selectedPayloads, payloadConfigs]);

  // Check if form is ready to proceed to next step
  const isReadyForNext = () => {
    const formData = form.getValues();
    const hasName = formData.name && formData.name.trim().length > 0;
    const hasPlatforms = formData.platforms && formData.platforms.length > 0;
    const hasPayloads = selectedPayloads.length > 0;
    const allPayloadsConfigured = unconfiguredPayloads.length === 0;
    
    return hasName && hasPlatforms && hasPayloads && allPayloadsConfigured;
  };

  const handleSelectPayload = (payloadId: string) => {
    const payload = getPayloadById(payloadId);
    
    if (selectedPayloads.includes(payloadId)) {
      // If removing a payload
      setSelectedPayloads(prev => prev.filter(id => id !== payloadId));
      return;
    }
    
    // If payload requires configuration
    if (payload && payload.configFields.length > 0) {
      // Store it as pending and open configuration dialog
      setPendingPayloadId(payloadId);
      setActivePayloadId(payloadId);
      setConfigDialogOpen(true);
    } else {
      // If no configuration needed, add it directly
      setSelectedPayloads(prev => [...prev, payloadId]);
      // Initialize empty config
      setPayloadConfigs(prev => ({
        ...prev,
        [payloadId]: {}
      }));
    }
  };

  const handlePayloadConfigChange = (payloadId: string, values: Record<string, any>) => {
    setPayloadConfigs(prev => ({
      ...prev,
      [payloadId]: values
    }));
  };

  const handleRemovePayload = (payloadId: string) => {
    setSelectedPayloads(prev => prev.filter(id => id !== payloadId));
    
    setPayloadConfigs(prev => {
      const newConfigs = { ...prev };
      delete newConfigs[payloadId];
      return newConfigs;
    });
  };

  const handleEditPayloadConfig = (payloadId: string) => {
    setActivePayloadId(payloadId);
    setConfigDialogOpen(true);
  };

  // Handle saving configuration from dialog
  const handleSaveConfig = () => {
    // Check if current configuration is valid before saving
    if (!configValidationState) {
      return; // Don't close dialog if validation fails
    }

    // If this was a pending payload, add it to selected payloads
    if (pendingPayloadId && !selectedPayloads.includes(pendingPayloadId)) {
      setSelectedPayloads(prev => [...prev, pendingPayloadId]);
    }
    
    setPendingPayloadId(null);
    setConfigDialogOpen(false);
  };

  // Handle canceling configuration dialog
  const handleCancelConfig = () => {
    // If this was a pending payload, don't add it
    if (pendingPayloadId && !selectedPayloads.includes(pendingPayloadId)) {
      // Remove any partial configuration
      setPayloadConfigs(prev => {
        const newConfigs = { ...prev };
        delete newConfigs[pendingPayloadId];
        return newConfigs;
      });
    }
    
    setPendingPayloadId(null);
    setConfigDialogOpen(false);
    setConfigValidationState(true); // Reset validation state
  };
  
  // Handle saving as draft
  const handleSaveAsDraft = () => {
    if (!form.formState.isValid) {
      form.trigger();
      return;
    }
    
    const data = form.getValues();
    
    // Create payload data for the profile
    const payloadsData = selectedPayloads.map(payloadId => ({
      payloadId,
      configValues: payloadConfigs[payloadId] || {}
    }));

    // Create the profile object
    const profileData: Omit<Profile, "id" | "createdAt" | "updatedAt"> = {
      name: data.name,
      description: data.description || "",
      platforms: data.platforms as Platform[],
      payloads: payloadsData,
      customer: customers.length > 0 ? customers[0] : undefined, // Use first customer for backward compatibility
      customers: customers, // Store all customers
      groups: groups,
      tags: tags
    };

    console.log("Saving profile draft:", profileData);

    if (onSave) {
      // Use the onSave callback to add the profile with draft flag
      const savedProfile = onSave(profileData, true);
      console.log("Saved profile draft:", savedProfile);
      
      toast.success("Profile saved as draft");
      
      // Move to assignment step after saving
      setStep("assignment");
    }
  };

  const handleNext = () => {
    if (!form.formState.isValid) {
      form.trigger();
      return;
    }
    
    setStep("assignment");
  };

  const handleBack = () => {
    setStep("details");
  };

  const onSubmit = (data: FormValues) => {
    // Check if there are unconfigured payloads that require configuration
    if (unconfiguredPayloads.length > 0) {
      toast.error("Some payloads require configuration", {
        description: "Please configure all payloads before submitting."
      });
      return;
    }

    // Create payload data for the profile
    const payloadsData = selectedPayloads.map(payloadId => ({
      payloadId,
      configValues: payloadConfigs[payloadId] || {}
    }));

    // Create the profile object
    const profileData: Omit<Profile, "id" | "createdAt" | "updatedAt"> = {
      name: data.name,
      description: data.description || "",
      platforms: data.platforms as Platform[],
      payloads: payloadsData,
      customer: customers.length > 0 ? customers[0] : undefined, // Use first customer for backward compatibility
      customers: customers, // Store all customers
      groups: groups,
      tags: tags
    };

    console.log("Profile data to save:", profileData);

    if (onSave) {
      // Use the onSave callback to add the profile
      const savedProfile = onSave(profileData);
      console.log("Saved profile:", savedProfile);
    }
    
    toast.success(
      isEdit ? "Profile updated successfully" : "Profile created successfully"
    );
    
    navigate("/profiles");
  };

  // For debugging - log the conditions for the Next button
  useEffect(() => {
    console.log("Next button conditions:", {
      isReadyForNext: isReadyForNext(),
      selectedPayloadsLength: selectedPayloads.length,
      unconfiguredPayloadsLength: unconfiguredPayloads.length,
      formIsValid: form.formState.isValid
    });
  }, [selectedPayloads, unconfiguredPayloads, form.formState.isValid]);

  return (
    <div className="h-full flex flex-col">
      {/* Fixed Header - matches main header exactly */}
      <div className="sticky top-0 bg-background z-10">
        <div className="flex items-center justify-between h-16 px-6 border-b border-border">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/profiles")}
              className="mr-3"
            >
              <X className="h-4 w-4" />
            </Button>
            <Separator orientation="vertical" className="h-6 mr-3" />
            <h1 className="text-lg font-bold">
              {isEdit ? "Edit Profile" : "Create New Profile"}
            </h1>
          </div>
          
          {/* Action buttons in header */}
          <div className="flex gap-2">
            {step === "details" ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveAsDraft}
                  disabled={!form.formState.isValid}
                >
                  Save as Draft
                </Button>
                <Button 
                  type="button"
                  onClick={handleNext}
                  disabled={!isReadyForNext()}
                >
                  Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            ) : (
              <>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button 
                  type="submit"
                  form="profile-form"
                  disabled={unconfiguredPayloads.length > 0}
                >
                  {isEdit ? "Update Profile" : "Create Profile"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <Form {...form}>
            <form id="profile-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              {step === "details" ? (
                <div className="space-y-8">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base font-bold">Profile Details</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <div className="space-y-6">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Profile name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Description</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Description of the profile and its purpose" 
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Payloads section with platforms and payload selector */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base font-bold">Payloads</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <div className="space-y-6">
                        {/* Supported Platforms section moved here */}
                        <FormField
                          control={form.control}
                          name="platforms"
                          render={() => (
                            <FormItem>
                              <div className="mb-2">
                                <FormLabel>Supported Platforms</FormLabel>
                                <FormDescription>
                                  Select which platforms this profile will support
                                </FormDescription>
                              </div>
                              <div className="grid grid-cols-2 gap-4 mt-2">
                                {platforms.map((platform) => (
                                  <FormField
                                    key={platform.id}
                                    control={form.control}
                                    name="platforms"
                                    render={({ field }) => (
                                      <FormItem
                                        key={platform.id}
                                        className="flex flex-row items-center space-x-3 space-y-0"
                                      >
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value?.includes(platform.id)}
                                            onCheckedChange={(checked) => {
                                              const value = field.value || [];
                                              return checked
                                                ? field.onChange([...value, platform.id])
                                                : field.onChange(
                                                    value.filter((v) => v !== platform.id)
                                                  );
                                            }}
                                          />
                                        </FormControl>
                                        <FormLabel className="font-normal">
                                          {platform.label}
                                        </FormLabel>
                                      </FormItem>
                                    )}
                                  />
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Payload selector - shown when platforms are selected */}
                        {form.watch("platforms") && form.watch("platforms").length > 0 && (
                          <div className="space-y-4 pt-4 border-t">
                            <PayloadTwoPanelSelector
                              selectedPlatforms={form.watch("platforms") as Platform[]}
                              selectedPayloads={selectedPayloads}
                              onSelectPayload={handleSelectPayload}
                              onConfigurePayload={handleEditPayloadConfig}
                            />
                            
                            {selectedPayloads.length > 0 && (
                              <div className="mt-4">
                                <h3 className="text-sm font-medium mb-2">Selected Payloads</h3>
                                <ScrollArea className="h-[200px]">
                                  <div className="space-y-2 pr-4">
                                    {selectedPayloads.map(payloadId => {
                                      const payload = getPayloadById(payloadId);
                                      if (!payload) return null;
                                      
                                      const configValues = payloadConfigs[payloadId] || {};
                                      const hasConfig = payload.configFields.length > 0;
                                      const needsConfig = hasConfig && Object.keys(configValues).length === 0;
                                      
                                      return (
                                        <div 
                                          key={payloadId} 
                                          className={`flex items-center justify-between p-3 border rounded-md ${needsConfig ? 'border-destructive' : 'bg-background'}`}
                                        >
                                          <div>
                                            <h4 className="font-medium flex items-center">
                                              {payload.name}
                                              {needsConfig && (
                                                <span className="text-xs text-destructive ml-2 bg-destructive/10 px-2 py-0.5 rounded-full">
                                                  Requires Configuration
                                                </span>
                                              )}
                                            </h4>
                                            <p className="text-sm text-muted-foreground">{payload.description}</p>
                                          </div>
                                          <div className="flex gap-2">
                                            {hasConfig && (
                                              <Button 
                                                variant={needsConfig ? "destructive" : "outline"} 
                                                size="sm"
                                                onClick={() => handleEditPayloadConfig(payloadId)}
                                              >
                                                Configure
                                              </Button>
                                            )}
                                            <Button 
                                              variant="destructive" 
                                              size="sm"
                                              onClick={() => handleRemovePayload(payloadId)}
                                            >
                                              Remove
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </ScrollArea>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="space-y-6">
                  <Card>
                    <CardContent className="pt-6">
                      <ProfileAssignment 
                        form={form}
                        setCustomers={setCustomers}
                        setGroups={setGroups}
                        initialCustomers={customers}
                        initialGroups={initialData?.groups}
                        setTags={setTags}
                        initialTags={initialData?.tags}
                        multipleCustomers={true}
                      />
                    </CardContent>
                  </Card>
                </div>
              )}
            </form>
          </Form>

          {/* Configuration Dialog */}
          <Dialog 
            open={configDialogOpen} 
            onOpenChange={(open) => {
              if (!open) {
                handleCancelConfig();
              }
            }}
          >
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>
                  Configure {activePayloadId ? getPayloadById(activePayloadId)?.name : 'Payload'}
                </DialogTitle>
              </DialogHeader>
              {activePayloadId && (
                <ScrollArea className="max-h-[60vh] pr-4">
                  <PayloadConfigForm
                    payloadId={activePayloadId}
                    initialValues={payloadConfigs[activePayloadId]}
                    onValuesChange={(values) => handlePayloadConfigChange(activePayloadId, values)}
                    onValidationChange={setConfigValidationState}
                    onRemove={() => {
                      setConfigDialogOpen(false);
                      handleRemovePayload(activePayloadId);
                    }}
                  />
                </ScrollArea>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleCancelConfig}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveConfig}
                  disabled={!configValidationState}
                >
                  Save Configuration
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
